<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="860px">
      <v-card>
        <v-card-title>
          <span>
            {{ dialogTitle }}
          </span>
        </v-card-title>

        <v-card-text>
          <v-form ref="form">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="form.label"
                  :label="
                    $t('wkt.admin.vacation-policy.tab-general.bag_hours_name')
                  "
                  :rules="[
                    labelRule,
                    (value) => validationRules.maxLength(value, 20),
                  ]"
                  :disabled="!canModify"
                />
              </v-col>
            </v-row>

            <v-row style="align-items: center;">
              <v-col cols="6">
                <v-select
                  v-if="canChangeUnit"
                  v-model="form.unit"
                  :items="unitItems"
                  item-text="text"
                  item-value="value"
                  :label="$t('wkt.admin.vacation-policy.bag-config.unit')"
                  :disabled="!canModify"
                />
                <v-text-field
                  v-else
                  :value="unitText"
                  :label="$t('wkt.admin.vacation-policy.bag-config.unit')"
                  readonly
                  disabled
                />
              </v-col>

              <v-col cols="3">
                <v-text-field
                  v-model="form.days"
                  type="number"
                  min="0"
                  step="1"
                  :label="annualValueLabel"
                  :rules="[validationRules.required, annualValueRule]"
                  :disabled="!canModify"
                />
              </v-col>

              <v-col cols="3">
                <v-btn
                  v-if="canModify"
                  text
                  small
                  color="#185EA9"
                  @click="openResetBalanceDialog"
                >
                  <span
                    style="text-decoration: underline; text-underline-offset: 2px;"
                  >
                    {{
                      $t(
                        "wkt.admin.vacation-policy.tab-general.start_bag_hours_balance",
                      ).toUpperCase()
                    }}
                  </span>
                </v-btn>
              </v-col>
            </v-row>

            <v-row align="center">
              <v-col cols="6">
                <v-switch
                  v-model="form.allowNegative"
                  hide-details
                  :label="
                    $t(
                      'wkt.admin.vacation-policy.tab-general.bag_hours_allow_negative',
                    )
                  "
                  :disabled="!canModify"
                />
              </v-col>

              <v-col cols="6">
                <v-switch
                  v-model="form.enabled"
                  hide-details
                  :label="$t('wkt.admin.vacation-policy.bag-config.enabled')"
                  :disabled="!canModify"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn v-if="canModify" color="#185EA9" text @click="ok">
            {{ $t("wkt.admin.common.dialog-buttons.save").toUpperCase() }}
          </v-btn>
          <v-btn text @click="showDialog = false">
            {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
          </v-btn>
        </v-card-actions>

        <LoadingDialog :loading="loading" />
      </v-card>
    </v-dialog>

    <VacationPolicyBalanceResetDialog ref="resetDialog" />
    <VacationPolicyUnitChangeDialog ref="unitChangeDialog" />
  </v-row>
</template>

<script>
import api from "../../../api/worktime";
import BaseModule from "../../common/BaseModule";
import LoadingDialog from "../../common/LoadingDialog";
import deepClone from "../../util/deep-clone";
import VacationPolicyBalanceResetDialog from "./VacationPolicyBalanceResetDialog";
import VacationPolicyUnitChangeDialog from "./VacationPolicyUnitChangeDialog";

const HOURS_BAG_UNIT_DAYS = 0;
const LICENSE_TYPE_FREE = 0;
const COMPANY_REFRESH_DELAY_MS = 1500;
const COMPANY_REFRESH_MAX_ATTEMPTS = 20;

export default {
  name: "VacationPolicyBagConfigDialog",
  mixins: [BaseModule],
  components: {
    LoadingDialog,
    VacationPolicyBalanceResetDialog,
    VacationPolicyUnitChangeDialog,
  },
  data() {
    return {
      showDialog: false,
      loading: false,
      canModify: false,
      licenseType: LICENSE_TYPE_FREE,
      company: null,
      definition: null,
      callback: null,
      originalUnit: HOURS_BAG_UNIT_DAYS,
      pendingCompanyPayload: null,
      pendingResetOptions: null,
      form: {
        label: "",
        days: 0,
        unit: HOURS_BAG_UNIT_DAYS,
        allowNegative: false,
        enabled: false,
      },
    };
  },
  computed: {
    canChangeUnit() {
      return this.canModify && Number(this.licenseType) !== LICENSE_TYPE_FREE;
    },
    unit() {
      if (this.form.unit == null) {
        return HOURS_BAG_UNIT_DAYS;
      }

      return Number(this.form.unit);
    },
    unitText() {
      return Number(this.unit) === 1
        ? this.$t("wkt.admin.vacation-policy.unit.hours")
        : this.$t("wkt.admin.vacation-policy.unit.days");
    },
    annualValueLabel() {
      return `${this.$t(
        "wkt.admin.vacation-policy.general.table.annual_value",
      )} (${this.unitText})`;
    },
    bagDisplayName() {
      if (this.definition && this.definition.rowLabel) {
        return this.definition.rowLabel;
      }

      if (this.form.label && this.form.label.trim()) {
        return this.form.label.trim();
      }

      return this.getBagOrderLabel();
    },
    dialogTitle() {
      if (!this.definition) {
        return this.$t("wkt.admin.vacation-policy.bag-config.dialog.title");
      }

      if (this.form.label && this.form.label.trim()) {
        return `${this.$t(
          "wkt.admin.vacation-policy.bag-config.dialog.title",
        )} · ${this.bagDisplayName}`;
      }

      return this.$t("wkt.admin.vacation-policy.bag-config.dialog.title");
    },
    unitItems() {
      return [
        { value: 0, text: this.$t("wkt.admin.vacation-policy.unit.days") },
        { value: 1, text: this.$t("wkt.admin.vacation-policy.unit.hours") },
      ];
    },
  },
  methods: {
    emitError(message) {
      if (this.callback) {
        this.callback({
          success: false,
          message,
        });
      }
    },
    open(company, definition, canModify, callback, options = {}) {
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      this.company = company ? deepClone(company) : null;
      this.definition = definition;
      this.canModify = canModify;
      this.callback = callback;
      this.licenseType =
        options.licenseType == null
          ? LICENSE_TYPE_FREE
          : Number(options.licenseType);
      this.originalUnit =
        this.company && definition && definition.unitProp
          ? Number(this.company[definition.unitProp] || HOURS_BAG_UNIT_DAYS)
          : HOURS_BAG_UNIT_DAYS;
      this.pendingCompanyPayload = null;
      this.pendingResetOptions = null;
      this.form = {
        label:
          this.company && definition
            ? this.company[definition.labelProp] || ""
            : "",
        days:
          this.company && definition
            ? Number(this.company[definition.daysProp] || 0)
            : 0,
        unit: this.originalUnit,
        allowNegative: Boolean(
          this.company &&
            definition &&
            this.company[definition.allowNegativeProp],
        ),
        enabled: Boolean(
          this.company && definition && this.company[definition.enabledProp],
        ),
      };
      this.showDialog = true;
    },
    labelRule(value) {
      if (!this.form.enabled) {
        return true;
      }

      return (
        !!(value && value.trim()) ||
        this.$t("wkt.admin.common.validation-utils.required_field")
      );
    },
    annualValueRule(value) {
      const numericValue = Number(value);
      const maximum = Number(this.unit) === 1 ? 8760 : 365;

      if (Number.isNaN(numericValue)) {
        return this.$t("wkt.admin.common.validation-utils.required_field");
      }

      if (!Number.isInteger(numericValue)) {
        return this.$t(
          "wkt.admin.vacation-policy.validation.integer_annual_value",
        );
      }

      return (
        (numericValue >= 0 && numericValue <= maximum) ||
        `${this.$t(
          "wkt.admin.common.validation-utils.required_field",
        )} [ 0 - ${maximum} ]`
      );
    },
    normalizeIntegerValue(value) {
      const numericValue = Number(value);
      return Number.isNaN(numericValue) ? 0 : Math.trunc(numericValue);
    },
    buildCompanyPayload() {
      const updatedCompany = deepClone(this.company);

      updatedCompany[this.definition.labelProp] = this.form.label
        ? this.form.label.trim()
        : "";
      updatedCompany[this.definition.daysProp] = this.normalizeIntegerValue(
        this.form.days,
      );
      updatedCompany[this.definition.allowNegativeProp] = Boolean(
        this.form.allowNegative,
      );
      updatedCompany[this.definition.enabledProp] = Boolean(this.form.enabled);

      if (this.definition.unitProp) {
        updatedCompany[this.definition.unitProp] = Number(
          this.form.unit || HOURS_BAG_UNIT_DAYS,
        );
      }

      return updatedCompany;
    },
    getBagOrderIndex() {
      if (!this.definition) {
        return null;
      }

      switch (Number(this.definition.id)) {
        case 7:
          return 0;
        case 1:
          return 1;
        case 2:
          return 2;
        case 3:
          return 3;
        case 4:
          return 4;
        case 5:
          return 5;
        default:
          return null;
      }
    },
    getBagOrderLabel() {
      const index = this.getBagOrderIndex();

      if (index == null) {
        return this.definition && this.definition.rowLabel
          ? this.definition.rowLabel
          : "";
      }

      return this.$i18n.locale === "en"
        ? `Bag ${index + 1}`
        : `Bolsa ${index + 1}`;
    },
    openResetBalanceDialog() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.$refs.resetDialog.open(
        {
          bagLabel: this.bagDisplayName,
        },
        (resetOptions) => {
          this.save(resetOptions);
        },
      );
    },
    ok() {
      this.save();
    },
    save(resetOptions = null) {
      if (!this.canModify) {
        this.showDialog = false;
        return;
      }

      if (!this.$refs.form.validate()) {
        return;
      }

      const updatedCompany = this.buildCompanyPayload();

      if (this.hasUnitChanged(updatedCompany)) {
        this.pendingCompanyPayload = updatedCompany;
        this.pendingResetOptions = resetOptions;
        this.$refs.unitChangeDialog.open(
          {
            sourceUnit: this.originalUnit,
            targetUnit: Number(
              updatedCompany[this.definition.unitProp] || HOURS_BAG_UNIT_DAYS,
            ),
          },
          (decision) => {
            this.handleUnitChangeDecision(decision);
          },
        );
        return;
      }

      this.performCompanyUpdate(updatedCompany, resetOptions);
    },
    hasUnitChanged(updatedCompany) {
      return (
        this.definition &&
        this.definition.unitProp &&
        Number(
          updatedCompany[this.definition.unitProp] || HOURS_BAG_UNIT_DAYS,
        ) !== Number(this.originalUnit)
      );
    },
    handleUnitChangeDecision(decision) {
      if (!decision || !this.pendingCompanyPayload) {
        this.pendingCompanyPayload = null;
        this.pendingResetOptions = null;
        return;
      }

      this.performUnitChange(this.pendingCompanyPayload, decision);
      this.pendingCompanyPayload = null;
      this.pendingResetOptions = null;
    },
    performCompanyUpdate(updatedCompany, resetOptions = null) {
      this.loading = true;

      api.updateMyCompany(
        updatedCompany,
        (response) => {
          if (!response || !response.rsOK) {
            this.loading = false;
            this.emitError(
              response
                ? response.message
                : this.$t("wkt.admin.common.error.save_server"),
            );
            return;
          }

          if (resetOptions) {
            this.executeUpdateBalance(
              updatedCompany,
              resetOptions,
              response.message,
            );
            return;
          }

          this.finalizeSuccess(updatedCompany, response.message);
        },
        (error) => {
          this.loading = false;
          this.emitError(this.getCallbackErrorMessage(
            error,
            this.$t("wkt.admin.common.error.save_server"),
          ));
        },
      );
    },
    performUnitChange(updatedCompany, decision) {
      this.loading = true;

      api.changeHoursBagUnit(
        {
          company: updatedCompany,
          hoursBagId: this.definition.id,
          sourceUnit: Number(this.originalUnit),
          targetUnit: Number(
            updatedCompany[this.definition.unitProp] || HOURS_BAG_UNIT_DAYS,
          ),
          action: decision.action,
          ratioHoursPerDay: decision.ratioHoursPerDay,
        },
        (response) => {
          if (response && response.resultCode === 100) {
            this.refreshCompanyAfterUnitChange(
              updatedCompany,
              this.$t("wkt.admin.vacation-policy.unit-change.processing_notice"),
              true,
            );
            return;
          }

          if (response && response.resultCode === -100) {
            this.loading = false;
            this.emitError(this.$t("wkt.admin.common.task_in_progress_error"));
            return;
          }

          if (!response || !response.rsOK) {
            this.loading = false;
            this.emitError(
              response
                ? response.message
                : this.$t("wkt.admin.common.error.save_server"),
            );
            return;
          }

              this.refreshCompanyAfterUnitChange(
            updatedCompany,
            this.$t("wkt.admin.vacation-policy.unit-change.completed_notice"),
                false,
          );
        },
        (error) => {
          this.loading = false;
          this.emitError(this.getCallbackErrorMessage(
            error,
            this.$t("wkt.admin.common.error.save_server"),
          ));
        },
      );
    },
    executeUpdateBalance(updatedCompany, resetOptions, baseMessage) {
      const filters = api.getCurrentAdminFilters(updatedCompany.id);

      api.updateBalanceVacation(
        {
          workplaceId: filters.workplaceId,
          departmentId: filters.departmentId,
          vacationPolicyId: null,
          hoursBagId: this.definition.id,
          hoursBagDays: this.normalizeIntegerValue(
            updatedCompany[this.definition.daysProp],
          ),
          accumulateVacation: Boolean(resetOptions.accumulateVacation),
        },
        (response) => {
          if (!response || !response.rsOK) {
            this.loading = false;
            this.emitError(
              response
                ? response.message
                : this.$t("wkt.admin.common.error.save_server"),
            );
            return;
          }

          this.finalizeSuccess(
            updatedCompany,
            response.message || baseMessage,
          );
        },
        (error) => {
          this.loading = false;
          this.emitError(this.getCallbackErrorMessage(
            error,
            this.$t("wkt.admin.common.error.save_server"),
          ));
        },
      );
    },
    refreshCompanyAfterUnitChange(
      fallbackCompany,
      message,
      waitForTargetUnit,
      attempt = 0,
    ) {
      api.getCompany(
        (company) => {
          if (
            waitForTargetUnit &&
            !this.hasRefreshedTargetUnit(company) &&
            attempt < COMPANY_REFRESH_MAX_ATTEMPTS
          ) {
            window.setTimeout(() => {
              this.refreshCompanyAfterUnitChange(
                fallbackCompany,
                message,
                waitForTargetUnit,
                attempt + 1,
              );
            }, COMPANY_REFRESH_DELAY_MS);
            return;
          }

          this.finalizeSuccess(company || fallbackCompany, message);
        },
        () => {
          if (waitForTargetUnit && attempt < COMPANY_REFRESH_MAX_ATTEMPTS) {
            window.setTimeout(() => {
              this.refreshCompanyAfterUnitChange(
                fallbackCompany,
                message,
                waitForTargetUnit,
                attempt + 1,
              );
            }, COMPANY_REFRESH_DELAY_MS);
            return;
          }

          this.finalizeSuccess(fallbackCompany, message);
        },
      );
    },
    hasRefreshedTargetUnit(company) {
      if (!company || !this.definition || !this.definition.unitProp) {
        return false;
      }

      return Number(company[this.definition.unitProp] || HOURS_BAG_UNIT_DAYS) === Number(this.form.unit || HOURS_BAG_UNIT_DAYS);
    },
    finalizeSuccess(updatedCompany, message) {
      this.loading = false;
      this.showDialog = false;
      this.originalUnit = Number(
        updatedCompany[this.definition.unitProp] || HOURS_BAG_UNIT_DAYS,
      );

      if (this.callback) {
        this.callback({
          success: true,
          company: updatedCompany,
          message,
        });
      }
    },
  },
};
</script>
